import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpGet } from 'utils';
import { valueWeightedPriceChange } from 'helpers/portfolioReturns';

export interface PortalHolding {
  portfolioId?: number;
  lwin18: string;
  wineName: string;
  vintage: number;
  region: string;
  cultWinesAllocationRegion: string;
  unit: string;
  qty: number;
  totalValue: number;
  totalCost: number;
  pnl: number;
  pnlPercent: number;
  marketPrice: number;
  marketPriceDate: string;
  // Price history
  changePercent1Month: number;
  changePercent3Months: number;
  changePercent6Months: number;
  changePercent12Months: number;
  // Ratings
  wa: number | null;
  vinous: string | null;
  // Vinous Review
  vReviewer: string | null;
  vReviewDate: string | null;
  vScoreMedian: number | null;
  vDrinkFrom: number | null;
  vDrinkTo: number | null;
  vTastingNote: string | null;
  vExternalLink: string | null;
  // Wine Advocate Review
  waReviewer: string | null;
  waScoreDate: string | null;
  waScore: number | null;
  waDrinkYearMin: number | null;
  waDrinkYearMax: number | null;
  waTastingNote: string | null;
  waArticleUrl: string | null;
  // Trading
  highestBidPrice: number | null;
  bidsCount: number | null;
  highestBid: {
    price: number;
    quantityOutstanding: number;
  } | null;
  lowestOffer: {
    price: number;
    quantityOutstanding: number;
  } | null;
  // Sales
  qtyForSale: number;
  // Quality Metrics (from API)
  grade: number | null;
  fairValueScore: number | null;
  liquidityScore: number | null;
  performanceScore: number | null;
  vintageScore: number | null;
  // Normalized critic score (combined WA/Vinous score)
  normalizedCriticScore: number | null;
}

interface PortalHoldingsResponse {
  data: PortalHolding[];
  count: number;
}

const fetchPortalHoldings = (portfolioId?: number) =>
  httpGet<PortalHoldingsResponse, { portfolioId?: number }>(
    'portal/PortalCurrentHoldings',
    portfolioId && portfolioId !== -1 ? { portfolioId } : undefined,
  );

export interface MarketInsight {
  lwin18: string;
  wineName: string;
  vintage: number;
  region: string;
  value: number;
  change: number;
  changePercent: number;
  rating?: number;
  bidPrice?: number;
}

export interface PortalHoldingsInsights {
  // Price momentum
  strongMomentum: MarketInsight[]; // 12-month gains > 10%
  weakMomentum: MarketInsight[]; // 12-month losses > 5%
  recentGainers: MarketInsight[]; // 1-month gains > 3%
  recentDecliners: MarketInsight[]; // 1-month losses > 3%

  // Ratings
  highlyRated: MarketInsight[]; // WA score >= 95
  criticsChoice: MarketInsight[]; // WA score 90-94

  // Trading activity
  withActiveBids: MarketInsight[];
  bidOpportunities: MarketInsight[]; // Bid price > cost basis

  // Summary stats
  // Value-weighted market price movement of the wines held today. NOT a portfolio return:
  // it excludes wines sold during the period and credits gains made before purchase. Use
  // helpers/portfolioReturns on the valuation series for actual performance.
  winePriceChange12Months: number | null;
  winePriceChange1Month: number | null;
  totalWithBids: number;
  avgRating: number;

  // Raw holdings data
  holdings: PortalHolding[];

  // Loading state
  isLoading: boolean;
  error: Error | null;

  // Lookup function
  getHoldingInsight: (lwin18: string) => PortalHolding | undefined;
}

export const usePortalHoldings = (
  portfolioId?: number | null,
  options?: { enabled?: boolean },
): PortalHoldingsInsights => {
  const effectivePortfolioId = portfolioId ?? -1;

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['portalCurrentHoldings', effectivePortfolioId],
    queryFn: () => fetchPortalHoldings(effectivePortfolioId),
    staleTime: 10 * 60 * 1000, // 10 minutes - this data doesn't change often
    cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    keepPreviousData: true, // Show previous data while fetching new portfolio
    enabled: options?.enabled ?? true,
  });

  const insights = useMemo(() => {
    const holdings = response?.data || [];

    if (holdings.length === 0) {
      return {
        strongMomentum: [],
        weakMomentum: [],
        recentGainers: [],
        recentDecliners: [],
        highlyRated: [],
        criticsChoice: [],
        withActiveBids: [],
        bidOpportunities: [],
        winePriceChange12Months: null,
        winePriceChange1Month: null,
        totalWithBids: 0,
        avgRating: 0,
      };
    }

    const toInsight = (h: PortalHolding, change: number, changePercent: number): MarketInsight => ({
      lwin18: h.lwin18,
      wineName: h.wineName,
      vintage: h.vintage,
      region: h.cultWinesAllocationRegion || h.region,
      value: h.totalValue,
      change,
      changePercent,
      rating: h.wa || undefined,
      bidPrice: h.highestBid?.price || h.highestBidPrice || undefined,
    });

    // Price momentum - 12 month
    const strongMomentum = holdings
      .filter((h) => h.changePercent12Months > 10)
      .sort((a, b) => b.changePercent12Months - a.changePercent12Months)
      .slice(0, 5)
      .map((h) => toInsight(h, h.changePercent12Months * h.totalValue * 0.01, h.changePercent12Months));

    const weakMomentum = holdings
      .filter((h) => h.changePercent12Months < -5)
      .sort((a, b) => a.changePercent12Months - b.changePercent12Months)
      .slice(0, 5)
      .map((h) => toInsight(h, h.changePercent12Months * h.totalValue * 0.01, h.changePercent12Months));

    // Recent changes - 1 month
    const recentGainers = holdings
      .filter((h) => h.changePercent1Month > 3)
      .sort((a, b) => b.changePercent1Month - a.changePercent1Month)
      .slice(0, 5)
      .map((h) => toInsight(h, h.changePercent1Month * h.totalValue * 0.01, h.changePercent1Month));

    const recentDecliners = holdings
      .filter((h) => h.changePercent1Month < -3)
      .sort((a, b) => a.changePercent1Month - b.changePercent1Month)
      .slice(0, 5)
      .map((h) => toInsight(h, h.changePercent1Month * h.totalValue * 0.01, h.changePercent1Month));

    // Ratings
    const highlyRated = holdings
      .filter((h) => h.wa && h.wa >= 95)
      .sort((a, b) => (b.wa || 0) - (a.wa || 0))
      .slice(0, 5)
      .map((h) => toInsight(h, h.pnl, h.pnlPercent * 100));

    const criticsChoice = holdings
      .filter((h) => h.wa && h.wa >= 90 && h.wa < 95)
      .sort((a, b) => (b.wa || 0) - (a.wa || 0))
      .slice(0, 5)
      .map((h) => toInsight(h, h.pnl, h.pnlPercent * 100));

    // Trading activity
    const withActiveBids = holdings
      .filter((h) => h.highestBid || h.highestBidPrice)
      .sort((a, b) => (b.highestBid?.price || b.highestBidPrice || 0) - (a.highestBid?.price || a.highestBidPrice || 0))
      .slice(0, 5)
      .map((h) => toInsight(h, h.pnl, h.pnlPercent * 100));

    // Bid opportunities - bid price > cost per unit
    const bidOpportunities = holdings
      .filter((h) => {
        const bidPrice = h.highestBid?.price || h.highestBidPrice || 0;
        const costPerUnit = h.totalCost / h.qty;
        return bidPrice > costPerUnit;
      })
      .sort((a, b) => {
        const aProfit = ((a.highestBid?.price || a.highestBidPrice || 0) - a.totalCost / a.qty) / (a.totalCost / a.qty);
        const bProfit = ((b.highestBid?.price || b.highestBidPrice || 0) - b.totalCost / b.qty) / (b.totalCost / b.qty);
        return bProfit - aProfit;
      })
      .slice(0, 5)
      .map((h) => toInsight(h, h.pnl, h.pnlPercent * 100));

    // Summary stats. Wine-price statistics, not portfolio returns - see valueWeightedPriceChange.
    const winePriceChange12Months = valueWeightedPriceChange(
      holdings.map((h) => ({ totalValue: h.totalValue, change: h.changePercent12Months })),
    );
    const winePriceChange1Month = valueWeightedPriceChange(
      holdings.map((h) => ({ totalValue: h.totalValue, change: h.changePercent1Month })),
    );

    const totalWithBids = holdings.filter((h) => h.highestBid || h.highestBidPrice).length;

    const ratedWines = holdings.filter((h) => h.wa && h.wa > 0);
    const avgRating =
      ratedWines.length > 0 ? ratedWines.reduce((sum, h) => sum + (h.wa || 0), 0) / ratedWines.length : 0;

    return {
      strongMomentum,
      weakMomentum,
      recentGainers,
      recentDecliners,
      highlyRated,
      criticsChoice,
      withActiveBids,
      bidOpportunities,
      winePriceChange12Months,
      winePriceChange1Month,
      totalWithBids,
      avgRating,
    };
  }, [response]);

  // Lookup function for individual wines
  const getHoldingInsight = useMemo(() => {
    const holdingsMap = new Map<string, PortalHolding>();
    (response?.data || []).forEach((h) => holdingsMap.set(h.lwin18, h));
    return (lwin18: string) => holdingsMap.get(lwin18);
  }, [response]);

  return {
    ...insights,
    holdings: response?.data || [],
    isLoading,
    error: error as Error | null,
    getHoldingInsight,
  };
};
